/**
 * Created by Ning Jiang on 2021-06-18.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {DeadwoodRip} from "../../../DeadwoodRip";
import {IBetWayWinData} from "../../../server/data/IDeadwoodRipData";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {DeadwoodRipSound} from "../../../sound/DeadwoodRipSound";
import {DeadwoodRipBackground} from "../../../view/DeadwoodRipBackground";

export class InitialWinController extends WinPresentationController {

    private _mainTimeline:TimelineLite | null;

    private _belowStakeWinRestriction:boolean;
    private _winLevel:number;

    constructor() {
        super("InitialWinController", true);
    }

    protected hasWin():boolean {
        return true;
    }

    protected async createWinAnimation():Promise<any> {
        DeadwoodRip.multiplier.verifyTotalMultiplier();
        if(DeadwoodRip.model.parsedGameData.betWins?.length > 0 && DeadwoodRip.model.parsedGameData.singleWin > 0) {
            DeadwoodRip.playScattersWin = true;
            this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue();
            this._winLevel = DeadwoodRip.model.parsedGameData.currentWinRatioConfig?.winLevel!;

            const tl = new TimelineLite();
            tl.add([
                this.getWinAnimation(),
                this.getWinCountUpAnimation()
            ]);
            this._mainTimeline = tl;
            await Promise.resolve(tl);
            this._mainTimeline = null;
        } else {
            return Promise.resolve();
        }
    }

    protected getWinAnimation():TimelineLite {
        const data = DeadwoodRip.model.parsedGameData;
        const tl = new TimelineLite();
        let winDelay = 0;
        let winWaysWon = 0;
        if(!this._belowStakeWinRestriction) {
            // TODO: play sound.
        }
        data.betWins.forEach(w => {
            winWaysWon += (<IBetWayWinData>w).numberOfCombinations;
            tl.add(this.getSingleBetWayWinAnimation(<IBetWayWinData>w, winWaysWon), winDelay);
            winDelay = (this._winLevel < 3 ? 0.01 : tl.totalDuration() - 0.5);
        });

        // play big win intro sound.
        if(data.currentWinRatioConfig!.bigWinLevel > -1) {
            tl.add(() => {
                DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.BIG_WIN_INTRO});
            }, `-=0.48`);
        }

        return tl;
    }

    protected getSingleBetWayWinAnimation(winData:IBetWayWinData, winWaysWon:number):TimelineLite {
        const tl = new TimelineLite();
        let symbolDelay = 0;
        const reelDelay = this.getReelDelay();
        const winTextPosition = DeadwoodRip.winView.getWinTextPosition(winData.reelWinPositions);
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            let hasReelWin = false;
            if(reelWin[0] && (DeadwoodRip.model.parsedGameData.evaluatedArea[reelId][0] === "WXN" || reelId == 4)) {
                hasReelWin = true;
                tl.add(this.getSymbolWinAnimation(reelId, 0, reelId === winTextPosition.x, winData), symbolDelay);
            } else {
                reelWin.forEach((winPosition, symId) => {
                    if(winPosition) {
                        hasReelWin = true;
                        tl.add(this.getSymbolWinAnimation(reelId, symId, reelId === winTextPosition.x && symId === winTextPosition.y, winData), symbolDelay);
                    }
                });
            }
            reelWin.forEach((winPosition, symId) => {
                if(winPosition) {
                    tl.add(DeadwoodRip.winView.getWinEffectAnimation(reelId, symId), symbolDelay);
                }
            });
            if(hasReelWin) {
                symbolDelay += reelDelay;
            }
        });

        if(!this._belowStakeWinRestriction) {
            tl.add(DeadwoodRip.waysWon.showWinWaysWon(winWaysWon, DeadwoodRip.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1), 0);
        }
        return tl;
    }

    protected getSymbolWinAnimation(reelId:number, symId:number, showWinText:boolean, winData:IBetWayWinData):TimelineLite {
        const tl = new TimelineLite();
        const sym = DeadwoodRip.reels[reelId].getSymbol(symId, false);
        if(!this._belowStakeWinRestriction) {
            tl.add([
                    () => {
                        DeadwoodRip.reels[reelId].reelView.addWinSymbol(sym);
                        if(showWinText) {
                            DeadwoodRip.winView.playInitialWinText(sym.getGlobalCenterPosition(), winData);
                        }
                        DeadwoodRipBackground.shakeStageOnce(Math.random() * 0.2);
                    },
                    sym.shotAtRandomPosition(true, false)
                ]
            );
        }

        return tl;
    }

    private getReelDelay():number {
        // tiny/small
        if(this._winLevel < 2) {
            return 0;
        }

        //mid
        if(this._winLevel < 3) {
            return 0.05;
        }

        // large and up
        return 0.1;
    }

    protected getWinCountUpAnimation():TimelineLite {
        const data = DeadwoodRip.model.parsedGameData;
        const tl = new TimelineLite();
        if(data.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }

        if(!this._belowStakeWinRestriction) {
            // TODO: if play sound inside the count up, pass in this._belowStakeWinRestriction to the countup.
            tl.add(DeadwoodRip.initialCountUp.getCountUpAnimation(0, data.singleWin, data.currentWinRatioConfig!.duration, data.currentMultiplier));
        }
        tl.add(() => SlotGame.keypadPlugin.setWin(data.totalWin, data.showTotalWin, BelowStakeWinRestriction.isWinBelowStake()));

        return tl;
    }

    protected skip() {
        DeadwoodRip.initialCountUp.skip();
        this._mainTimeline?.progress(1);
    }
}